import React, { useEffect, useState } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import apiDefinitions from '../../../../../core/api/apiDefinitions'; // 🔺 adjust path if needed

interface Group {
  id: number;
  name: string;
  description?: string;
  status?: string;
}

interface Department {
  id: number;
  name: string;
}

interface FormData {
  employeeNo: string;
  userType: string;
  status: string;
  departmentNames: string[];
  licenseNumber: string;
  licenseType: string;
  expireDate: string;
}

interface EditUserModalProps {
  show: boolean;
  onHide: () => void;
  formData: FormData;
  groups: Group[];
  handleInputChange: React.ChangeEventHandler<
    HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
  >;
  handleDepartmentChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  handleUpdateUser: (e: React.FormEvent) => void;
  loading: boolean;
}

const DEPT_PAGE_SIZE = 15;

const EditUserModal: React.FC<EditUserModalProps> = ({
  show,
  onHide,
  formData,
  groups,
  handleInputChange,
  handleDepartmentChange,
  handleUpdateUser,
  loading,
}) => {
  // 🔁 Departments from API (paginated)
  const [departments, setDepartments] = useState<Department[]>([]);
  const [deptTotal, setDeptTotal] = useState<number>(0);
  const [deptPage, setDeptPage] = useState<number>(1);
  const [deptSearch, setDeptSearch] = useState<string>('');
  const [deptLoading, setDeptLoading] = useState<boolean>(false);
  const [deptError, setDeptError] = useState<string | null>(null);

  const totalDeptPages = deptTotal > 0 ? Math.ceil(deptTotal / DEPT_PAGE_SIZE) : 1;

  const shouldLoadDepartments =
    formData.userType === 'DEPARTMENT_HEAD' || formData.userType === 'USER';

  // 🔁 Fetch departments when modal open + userType relevant + page/search change
  useEffect(() => {
    if (!show || !shouldLoadDepartments) return;

    const fetchDepartments = async () => {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        setDeptError('No authentication token found. Please log in.');
        return;
      }

      try {
        setDeptLoading(true);
        setDeptError(null);

        const payload = {
          filterType: deptSearch ? 'NAME' : null,
          filterValue: deptSearch || null,
          pageSize: DEPT_PAGE_SIZE,
        };

        const res = await apiDefinitions.getDepartments(deptPage, payload);

        if (res.data.status === 'S1000') {
          const depList = Array.isArray(res.data.data?.departments)
            ? res.data.data.departments
            : [];
          setDepartments(depList);
          setDeptTotal(res.data.data?.total ?? depList.length);
          if (depList.length === 0) {
            setDeptError('No departments found.');
          }
        } else {
          setDeptError(res.data.statusDescription || 'Failed to fetch departments.');
        }
      } catch (err: any) {
        setDeptError(
          err?.response?.data?.statusDescription || 'Error fetching departments.'
        );
      } finally {
        setDeptLoading(false);
      }
    };

    fetchDepartments();
  }, [show, shouldLoadDepartments, deptPage, deptSearch]);

  const handleClose = () => {
    // reset local department UI state when closing
    setDeptSearch('');
    setDeptPage(1);
    setDepartments([]);
    setDeptError(null);
    setDeptTotal(0);
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Edit User</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleUpdateUser}>
          <Form.Group className="mb-3">
            <Form.Label>Employee Number</Form.Label>
            <Form.Control
              type="text"
              name="employeeNo"
              value={formData.employeeNo}
              readOnly
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>User Type</Form.Label>
            <Form.Select
              name="userType"
              value={formData.userType}
              onChange={(e) => {
                // Reset department paging/search when userType changes
                setDeptPage(1);
                setDeptSearch('');
                handleInputChange(e);
              }}
              required
            >
              <option value="">Select User Type</option>
              {groups.map((group) => (
                <option
                  key={group.id}
                  value={group.name.replace('_K', '')}
                >
                  {group.name.replace('_K', '')}
                </option>
              ))}
            </Form.Select>
            {!formData.userType && (
              <Form.Text className="text-danger">
                Please select a valid user type.
              </Form.Text>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Status</Form.Label>
            <Form.Select
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              required
            >
              <option value="">Select Status</option>
              {['ACTIVE', 'BLOCKED'].map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          {(formData.userType === 'DEPARTMENT_HEAD' ||
            formData.userType === 'USER') && (
            <Form.Group className="mb-3">
              <Form.Label>Department</Form.Label>

              {/* 🔍 search bar for departments (server-side filter) */}
              <Form.Control
                className="mb-2"
                type="text"
                placeholder="Search department by name"
                value={deptSearch}
                onChange={(e) => {
                  setDeptSearch(e.target.value);
                  setDeptPage(1);
                }}
              />

              {deptLoading && (
                <div className="mb-2">
                  <small className="text-muted">Loading departments...</small>
                </div>
              )}
              {deptError && (
                <div className="mb-2">
                  <small className="text-danger">{deptError}</small>
                </div>
              )}

              <Form.Select
                name="departmentNames"
                value={formData.departmentNames[0] || ''}
                onChange={handleDepartmentChange}
                required
                disabled={deptLoading || departments.length === 0}
              >
                <option value="">Select Department</option>
                {departments.map((dept) => (
                  <option key={dept.id} value={dept.name}>
                    {dept.name}
                  </option>
                ))}
              </Form.Select>

              {/* Pagination controls for departments */}
              <div className="d-flex justify-content-between align-items-center mt-2">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={deptPage <= 1}
                  onClick={() => setDeptPage((prev) => Math.max(prev - 1, 1))}
                >
                  Previous
                </Button>
                <small>
                  Page {deptPage} of {totalDeptPages}
                </small>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={deptPage >= totalDeptPages}
                  onClick={() =>
                    setDeptPage((prev) =>
                      prev < totalDeptPages ? prev + 1 : prev
                    )
                  }
                >
                  Next
                </Button>
              </div>

              {formData.departmentNames.length === 0 && (
                <Form.Text className="text-danger">
                  Please select a department.
                </Form.Text>
              )}
            </Form.Group>
          )}

          {formData.userType === 'DRIVER' && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>License Number</Form.Label>
                <Form.Control
                  type="text"
                  name="licenseNumber"
                  value={formData.licenseNumber}
                  onChange={handleInputChange}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>License Type</Form.Label>
                <div>
                  {['Heavy', 'Normal'].map((type) => (
                    <Form.Check
                      inline
                      key={type}
                      type="radio"
                      label={type}
                      name="licenseType"
                      id={`license-${type}`}
                      value={type}
                      checked={formData.licenseType === type}
                      onChange={handleInputChange}
                      required
                    />
                  ))}
                </div>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>License Expiration Date</Form.Label>
                <Form.Control
                  type="date"
                  name="expireDate"
                  value={formData.expireDate}
                  onChange={handleInputChange}
                  required
                />
              </Form.Group>
            </>
          )}

          <Button variant="secondary" type="submit" disabled={loading}>
            Update User
          </Button>
          <Button
            variant="primary"
            onClick={handleClose}
            className="ms-2"
            disabled={loading}
          >
            Cancel
          </Button>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default EditUserModal;
